/**
 * 「历史快照」区的**文案唯一来源**（N 域 N4 · `AC-N4.21` 的端侧半边）
 *
 * 依据：N 域 PRD **§7.4 `AC-N4.21`**（原文：「覆盖前快照**用户可见、可回滚**」· 验收 = UI 走查 · **必测**）
 * · **`DR-039`**（覆盖前快照载体与回滚口径 · 已拍板生效）· N 域 PRD **§5.4.2**（原型 `08` 面板 3）。
 *
 * ## 🔴 本文件的每一条文案都有"为什么是这句"的依据
 *
 * | 文案 | 依据 |
 * |---|---|
 * | 区标题 / 说明 | `DR-039` **D6**：逐行显示 `时间 / 来源 / 体积`，两个动作 **下载** · **回滚到此** |
 * | 回滚二次确认的三段 | `DR-039` **D5** + `snapshot.service.ts` 的 `rollback()` 文档：「回滚 = 一次覆盖」 |
 * | 「覆盖前会自动留一份」 | `DR-039` **§4.3**（回滚前落 `beforeRollback` 快照，防递归死局）—— 🔴 这是**必须说清**的事实：它决定了"滚错了还能再滚回来" |
 *
 * ## ⚠️ 两处**刻意不写**的文案（都不是遗漏）
 *
 * | 不写 | 为什么 |
 * |---|---|
 * | 🔴 **`reason` 的中文**（覆盖前 / 回滚前） | 它是**共享层** `snapshotReasonLabel()` 的职责（`contract/snapshot.ts` 明文"端侧直出"）⇒ 在这里再写一份 = 同一事实两处各答一遍（铁律 24） |
 * | 🔴 **保留份数 `5`** | 它随列表**一起下发**（`SnapshotListView.keepLimit`）⇒ 端侧写死会让服务端调整额度时界面**静默说谎**（契约文件头原文） |
 *
 * 📌 这与 `sovereignty-labels.ts` 的做法一致：**能算的都算，不能算的才写**。
 */

/** 区标题（`DR-039` D6「历史快照」） */
export const SNAPSHOT_SECTION_TITLE = '历史快照';

/**
 * 区说明 —— 🔴 三件事都要说清：**何时自动落** · **最多几份** · **它能干什么**。
 *
 * ⚠️ 份数**不在这里**（它由 {@link snapshotSectionNote} 用服务端下发的 `keepLimit` 拼）——
 * 这条只讲"机制"，不碰数字。
 */
export const SNAPSHOT_SECTION_NOTE =
  '执行「覆盖」恢复前会自动留一份，回滚前也会 —— 所以回滚本身也能再滚回来。每份都是一份完整备份，可下载带走。';

/** 区说明（带份数 · 🔴 份数**由服务端下发**，不写死） */
export function snapshotSectionNote(keepLimit: number): string {
  return `最多保留最近 ${String(keepLimit)} 份，更早的会自动清掉；需要长期保存请点「下载」。`;
}

/** 列表为空的说明（`AC-N4.21` 之前落地的账本会看到这个） */
export const SNAPSHOT_EMPTY_NOTE =
  '还没有快照。执行一次「覆盖」恢复或回滚之后，这里会出现还原点。';

/** owner 之外的成员看到的那句（三条快照路由都挂 `@RequireOwner(BulkOverwriteImport)`） */
export const SNAPSHOT_OWNER_ONLY_NOTE =
  '只有账本所有者可以查看与回滚快照（回滚是破坏性动作，可能清空本账本内容）。';

/** 每行的两个动作 */
export const SNAPSHOT_DOWNLOAD_ACTION = '下载';
export const SNAPSHOT_ROLLBACK_ACTION = '回滚到此';

/** 下载中 / 回滚中（按钮忙碌态） */
export const SNAPSHOT_DOWNLOAD_BUSY = '下载中…';
export const SNAPSHOT_ROLLBACK_BUSY = '回滚中…';

// ─────────────────────── 回滚的二次确认（🔴 破坏性动作） ───────────────────────

/** 确认对话框标题 */
export const ROLLBACK_DIALOG_TITLE = '回滚到此快照？';

/** 确认对话框的第 1 段：会发生什么（`DR-039` D5 —— 回滚 = 一次覆盖） */
export const ROLLBACK_IMPACT_NOTE =
  '回滚会用这份快照覆盖当前账本的全部数据 —— 之后的数据、之后记的账都会消失。这是一次「覆盖」恢复，和上传备份选覆盖模式是同一件事。';

/**
 * 确认对话框的第 2 段：🔴 **安全网**（`DR-039` §4.3）。
 *
 * 这句是**必须**的：它决定了用户敢不敢点。若不说，用户会以为"回滚是单向的"。
 */
export const ROLLBACK_SAFETY_NOTE =
  '放心：回滚前系统会自动给「现在」也留一份快照。滚错了可以再滚回来。';

/** 快照时刻与来源的展示前缀（行内） */
export const SNAPSHOT_TIME_LABEL = '时刻';
export const SNAPSHOT_REASON_LABEL = '来源';
export const SNAPSHOT_SIZE_LABEL = '体积';

/** 取消 / 确认 */
export const ROLLBACK_CANCEL = '再想想';
export const ROLLBACK_CONFIRM = '确认回滚';

/** 回滚成功后的提示（`RestoreResultView` 已给出逐账户余额与完整性三项 —— 见 `RestoreDialog` 的渲染） */
export const ROLLBACK_DONE_NOTE = '已回滚。回滚前的那份数据已存为一份新快照，可再滚回来。';

/** 回滚失败前缀（🔴 后接**服务端 `message` 原文**，不自己编 —— 见 `http.ts` 文件头） */
export const ROLLBACK_FAILED_PREFIX = '回滚失败：';
